import { Component, Host, h, Prop, State, Listen } from '@stencil/core';
import { Memoize, parseIfString } from '@springbokagency/stencil-base';
import { Splide } from '@splidejs/splide';
import { ExternalArticleSectionData } from './external-article-section.types';

/**
 * External article section.
 */
@Component({
  tag: 'efb-external-article-section',
  styleUrl: 'external-article-section.scss',
  shadow: true,
})
export class EfbExternalArticleSection {
  private sliderElement!: HTMLElement;

  private slider: Splide;

  /**
   * State
   */

  @State() isArticleModalOpen: boolean = false;

  @State() modalActiveItem: string;

  /**
   * Component's data.
   */
  @Prop() data: ExternalArticleSectionData | string;

  @Memoize('data')
  get parsedData(): ExternalArticleSectionData {
    return parseIfString(this.data);
  }

  /**
   * Events
   */

  @Listen('efbClickAction')
  onEfbClickActionHandler(e: CustomEvent) {
    const { name, value } = e.detail;

    if (name === 'open-article-modal') {
      this.isArticleModalOpen = true;

      this.modalActiveItem = value;
    }
  }

  @Listen('closeModal')
  closeModalHandler(e: CustomEvent) {
    const { modalId } = e.detail;

    if (modalId === 'article-modal') {
      this.isArticleModalOpen = false;
    }
  }

  /**
   * Lifecycle Hooks
   */

  componentDidLoad() {
    this.slider = new Splide(this.sliderElement, {
      type: 'slide',
      fixedWidth: 352,
      gap: 32,
      pagination: true,
      focus: 0,
      omitEnd: true,
      breakpoints: {
        768: {
          fixedWidth: 296,
          gap: 16,
        },
      },
    }).mount();
  }

  disconnectedCallback() {
    this.slider.destroy();
  }

  render() {
    const { heading, text, slider } = this.parsedData;

    return (
      <Host>
        <div class="external-article-section">
          <div class="container">
            <div class="head">
              <efb-title tag="h2" text={heading} size="sm" bottomMargin="md" bold />
              <efb-paragraph
                style={{ maxWidth: '483px' }}
                text={text}
                size="md"
                bottomMargin="lg"
              />
            </div>
            <div class="body">
              {/* Splide */}
              <div
                class="splide"
                role="group"
                aria-label="Products slider"
                ref={(el) => {
                  this.sliderElement = el as HTMLElement;
                }}
              >
                {/* Slides */}
                <div class="splide__track">
                  <div class="splide__list">
                    {slider.map((item) => (
                      <efb-external-article-slide
                        class="splide__slide"
                        {...item}
                      ></efb-external-article-slide>
                    ))}
                  </div>
                </div>

                {/* Arrows */}
                <div class="splide__arrows">
                  <button class="splide__arrow splide__arrow--prev">
                    <efb-icon name="back-arrow" />
                  </button>
                  <button class="splide__arrow splide__arrow--next">
                    <efb-icon name="forward-arrow" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modal */}
        {this.isArticleModalOpen && (
          <efb-article-modal
            data={JSON.stringify(this.parsedData.modal)}
            activeItem={this.modalActiveItem}
          ></efb-article-modal>
        )}
      </Host>
    );
  }
}
